import { useEffect } from 'react';
import type { ShouldRevalidateFunction } from 'react-router';
import type BoltMetaArgs from 'types/BoltMetaArgs';
import type { HomePage } from 'types/capi/homePage';

import HomepageViewEvent from '~/events/HomepageViewEvent';
import useEnv from '~/hooks/useEnv';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import useViewEvent from '~/hooks/useViewEvent';
import CopOrDrop from '~/modules/CopOrDrop';
import Splash from '~/modules/Splash';
import { getAlternateTagsForAllRegions } from '~/utils/alternateTags';
import seo from '~/utils/seo';

import AppBanner from '../../modules/AppBanner';
import ArticlesSection from '../../modules/ArticlesSection';
import BannerCarousel from '../../modules/BannerCarousel';
import HeaderCarousel from '../../modules/HeaderCarousel';
import HotDeals from '../../modules/HotDeals';
import MarketplaceTopSellers from '../../modules/MarketplaceTopSellers';
import PopularBrands from '../../modules/PopularBrands';
import PopularProducts from '../../modules/PopularProducts';
import ProductList from '../../modules/ProductList';
import SocialMediaCarousel from '../../modules/SocialMediaCarousel';
import SubBrands from '../../modules/SubBrands';
import TopCategories from '../../modules/TopCategories';
import UpcomingProducts from '../../modules/UpcomingProducts';
import Usps from '../../modules/Usps';
import LaunchPage from './LaunchPage';

export const handle = { i18n: ['home'] };

export default function Home() {
  useViewEvent({ viewType: 'homepage' });

  const env = useEnv();
  const { modules, _schemas } = usePageData<HomePage>();

  useStructuredData(_schemas);

  const region = useRegion();
  const user = useUser();
  const { clientIp, isBot } = useRootData();

  useEffect(() => {
    new HomepageViewEvent({
      clientIp: clientIp ?? undefined,
      isBot,
      user
    }).send();
  }, [clientIp, isBot, user]);

  if (region.code !== 'GB' && !env.FF_INTERNATIONAL_HOMEPAGE)
    return <LaunchPage />;

  return (
    <>
      <h1 className="hidden">The Sole Supplier</h1>
      {modules.map((module, i) => {
        switch (module.type) {
          case 'APP_BANNER':
            return (
              <AppBanner key={`${module.type}-${i}`} items={module.items} />
            );
          case 'ARTICLES_CAROUSEL':
            return (
              <ArticlesSection
                key={`${module.type}-${i}`}
                items={module.items}
                layout="carousel"
                modulePosition={i + 1}
              />
            );
          case 'COP_OR_DROP':
            return <CopOrDrop key={`${module.type}-${i}`} />;
          case 'HEADER_CAROUSEL':
            return (
              <HeaderCarousel
                variant="base"
                analytics={{ id: 'HOMEPAGE_CAROUSEL', position: 'Homepage' }}
                key={`${module.type}-${i}`}
                module={module}
              />
            );
          case 'HOT_DEALS':
            return (
              <HotDeals
                key={`${module.type}-${i}`}
                items={module.items}
                modulePosition={i + 1}
                pageType="homepage"
              />
            );
          case 'LATEST_ARTICLES_DUO':
            return (
              <ArticlesSection
                key={`${module.type}-${i}`}
                items={module.items}
                layout="duo"
                modulePosition={i + 1}
              />
            );
          case 'LATEST_ARTICLES_GRID':
            return (
              <ArticlesSection
                key={`${module.type}-${i}`}
                items={module.items}
                layout="tiles"
                modulePosition={i + 1}
              />
            );
          case 'LATEST_ARTICLES_HEADLINES':
            return (
              <ArticlesSection
                key={`${module.type}-${i}`}
                items={module.items}
                layout="headlines"
                modulePosition={i + 1}
              />
            );
          case 'LATEST_ARTICLES_HIGHLIGHT':
            return (
              <ArticlesSection
                key={`${module.type}-${i}`}
                items={module.items}
                layout="highlights"
                modulePosition={i + 1}
              />
            );
          case 'LATEST_FEATURE':
            return (
              <ArticlesSection
                article={module.article}
                key={`${module.type}-${i}`}
                layout="feature"
                modulePosition={i + 1}
              />
            );
          case 'MARKETPLACE_TOP_SELLERS':
            return (
              <MarketplaceTopSellers
                key={`${module.type}-${i}`}
                items={module.items}
                backdrop={module.backdrop}
                modulePosition={i + 1}
                pageType="homepage"
              />
            );
          case 'POPULAR_BRANDS':
            return (
              <PopularBrands
                key={`${module.type}-${i}`}
                brands={module.items}
              />
            );
          case 'POPULAR_PRODUCTS':
            return (
              <PopularProducts
                key={`${module.type}-${i}`}
                items={module.items}
                modulePosition={i + 1}
                pageType="homepage"
              />
            );
          case 'PRODUCT_LIST':
            return (
              <ProductList
                key={`${module.type}-${i}`}
                module={module}
                modulePosition={i + 1}
                moduleType="homepage-product-list"
                pageType="homepage"
              />
            );
          case 'SEASONAL_BANNER':
            return (
              <BannerCarousel key={`${module.type}-${i}`} slides={[module]} />
            );
          case 'SOCIAL_MEDIA_CAROUSEL':
            return (
              <SocialMediaCarousel
                key={`${module.type}-${i}`}
                module={module}
              />
            );
          case 'SPLASH':
            return <Splash key={`${module.type}-${i}`} module={module} />;
          case 'SUB_BRANDS':
            return (
              <SubBrands key={`${module.type}-${i}`} subBrands={module.items} />
            );
          case 'TOP_CATEGORIES':
            return (
              <TopCategories key={`${module.type}-${i}`} categories={module} />
            );
          case 'UPCOMING_PRODUCTS':
            return (
              <UpcomingProducts
                key={`${module.type}-${i}`}
                items={module.items}
                modulePosition={i + 1}
                pageType="homepage"
              />
            );
          case 'USPS':
            return <Usps key={`${module.type}-${i}`} data={module} />;
          default:
            return null;
        }
      })}
    </>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (formAction?.startsWith('/bolt/cod')) return false;

  return defaultShouldRevalidate;
};

type LoaderData = {
  pageData: HomePage;
};

export const meta = (metaArgs: BoltMetaArgs<LoaderData>) => {
  const { data } = metaArgs;

  const withSeo = data
    ? seo.formatForMeta(data.pageData.canonical_url, data.pageData.seo)
    : [];

  return withSeo.concat(getAlternateTagsForAllRegions(metaArgs));
};

export { loader } from './loader.server';
